import React, { useContext, useEffect, useState } from 'react';
import {
    Badge,
    Box,
    Heading,
    HStack,
    Icon,
    Spinner,
    Text,
    VStack,
    Wrap,
    WrapItem,
} from '@chakra-ui/react';
import { FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import KpiProvenanceTag from './KpiProvenanceTag';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

const ROLE_LABELS = {
    Admin: 'Admin',
    'Relationship Manager': 'Relationship Manager',
    'Head of Advisory': 'Head of Advisory',
    'Portfolio Adviser': 'Portfolio Adviser',
    Compliance: 'Compliance',
    'View Only': 'View Only',
};

const DataReadinessStrip = ({ role }) => {
    const { token, user } = useContext(AuthContext);
    const [sufficiency, setSufficiency] = useState(null);
    const [loading, setLoading] = useState(true);

    const subscription = user?.subscription;

    useEffect(() => {
        if (!token || !['solo', 'pro', 'business'].includes(subscription)) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        const fetch = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/content/product-data/sufficiency`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!cancelled) {
                    setSufficiency(response.data || null);
                }
            } catch (e) {
                // silently fail - readiness is best-effort
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        fetch();
        return () => { cancelled = true; };
    }, [token, subscription]);

    if (!['solo', 'pro', 'business'].includes(subscription)) {
        return null;
    }
    if (loading) {
        return (
            <Box border="1px solid" borderColor="gray.100" borderRadius="lg" bg="white" px={4} py={2} mb={4}>
                <HStack><Spinner size="xs" /><Text fontSize="xs" color="gray.500">Loading data readiness…</Text></HStack>
            </Box>
        );
    }
    if (!sufficiency) {
        return null;
    }
    const readiness = sufficiency.role_readiness?.find((row) => row.role === role);
    if (!readiness) {
        return null;
    }
    const nextBest = sufficiency.next_best_uploads || [];

    return (
        <Box
            bg="white"
            border="1px solid"
            borderColor="gray.100"
            borderRadius="xl"
            p={4}
            mb={4}
        >
            <HStack justify="space-between" align="start" flexWrap="wrap" gap={2}>
                <Box>
                    <HStack spacing={2} mb={2}>
                        <Icon as={FiCheckCircle} color="brand.navy" />
                        <Heading size="xs" color="gray.700">
                            {ROLE_LABELS[role] || role} data readiness
                        </Heading>
                        <KpiProvenanceTag status={readiness.status} />
                    </HStack>
                    <Wrap spacing={2}>
                        {readiness.computed?.map((item) => (
                            <WrapItem key={`c-${item}`}>
                                <Badge colorScheme="green" variant="subtle" borderRadius="full">{item}</Badge>
                            </WrapItem>
                        ))}
                        {readiness.inferred?.map((item) => (
                            <WrapItem key={`i-${item}`}>
                                <Badge colorScheme="yellow" variant="subtle" borderRadius="full">{item}</Badge>
                            </WrapItem>
                        ))}
                        {readiness.unavailable?.map((item) => (
                            <WrapItem key={`u-${item}`}>
                                <Badge colorScheme="red" variant="subtle" borderRadius="full">missing: {item}</Badge>
                            </WrapItem>
                        ))}
                    </Wrap>
                </Box>
                {nextBest.length > 0 && (
                    <Box maxW={{ base: 'full', md: '50%' }}>
                        <HStack spacing={2} mb={1}>
                            <Icon as={FiAlertTriangle} color="orange.500" />
                            <Text fontSize="xs" fontWeight="semibold" color="orange.700">
                                Next best upload
                            </Text>
                        </HStack>
                        <VStack align="stretch" spacing={0.5}>
                            {nextBest.slice(0, 2).map((message) => (
                                <Text key={message} fontSize="xs" color="orange.700">• {message}</Text>
                            ))}
                        </VStack>
                    </Box>
                )}
            </HStack>
        </Box>
    );
};

export default DataReadinessStrip;
